import { useMemo } from "react";
import { useRoom } from "../pages/floor/RoomProvider";
import { getOperationalState } from "./useLiveTrips";

/**
 * useAttentionQueue — the answer-in-two-seconds engine.
 *
 * Synthesizes the seven questions the dispatcher must answer instantly:
 *   1. What needs me now?             → needs      (pending offers, unassigned, SOS, expired)
 *   2. Who is about to become a problem?→ risks     (late-warning, storm-exposed, TTL near expiry)
 *   3. Which provider is failing?     → failing_tps (recent declines / no-shows > threshold)
 *   4. Which driver is late?          → late_drivers (behind PU/DO by wall-clock)
 *   5. What should I do next?         → next_action (single most-urgent needs[] entry)
 *   6. What did AI already handle?    → ai_actions  (Nova whispers with kind=ai in last 5min)
 *   7. What changed in the last minute?→ minute_changes (state deltas within 60s)
 *
 * All derived from state already loaded by `useLiveTrips`, `useKaizenLiveOffers`,
 * and `RoomProvider.network`. No new fetches. Under 2s = under 1s.
 */
export function useAttentionQueue() {
  const {
    allTrips = [],
    incomingOffers = [],
    network,
    sosTripId,
    chainProposals = [],
    chainAlerts = [],
    tripRiskAlerts = [],
  } = useRoom();

  return useMemo(() => {
    const now = Date.now();
    const MIN = 60 * 1000;

    // ─── 1 · NEEDS ME NOW ─────────────────────────────────────
    const needs = [];
    // SOS is always highest priority
    if (sosTripId) needs.push({
      kind: "sos", severity: "critical", attention_score: 100,
      label: `SOS · ${String(sosTripId).slice(0, 8)}`,
      trip_id: sosTripId, sub: "human must acknowledge",
    });
    // Kaizen offers — every visible one is a decision that needs you.
    // Same RED band as NEEDS_TRIAGE in the operational state engine.
    incomingOffers.forEach(o => {
      const rid = (o.kaizen_offer_id || "").replace(/^kaizen-/, "");
      const age = ageSeconds(o.last_seen_at || o.ingested_at);
      const off = o.offer || {};
      needs.push({
        kind: "kaizen_offer", severity: age > 30 ? "high" : "med",
        attention_score: Math.min(90, 85 + Math.floor((age || 0) / 30)),
        label: `Kaizen ${rid} · ${off.mobility_type || "amb"}`,
        sub: age != null ? `${age}s old` : "new",
        offer_id: rid, age,
      });
    });
    // Nova's chain proposals — a multi-stop route ready for one-tap approval.
    chainProposals.forEach(c => {
      const first = c.legs?.[0];
      const sd = c.suggested_driver;
      needs.push({
        kind: "chain_proposal", severity: "med", attention_score: 78,
        label: `nova's route · ${c.legs?.length || 0} stops · ${first?.rider_name || "rider"}`,
        sub: sd ? `suggests ${sd.name} (${sd.distance_mi}mi away) · score ${c.feasibility_score}` : "no driver suggestion yet",
        chain_id: c.chain_id, suggested_driver: sd,
      });
    });
    // Unassigned trips (REQUESTED / TP_ACCEPTED / EXPIRED_OFFER without driver)
    // — but only ones with a pickup time inside the actionable window.
    allTrips.forEach(t => {
      const s = String(t.status || "").toUpperCase();
      if (!["REQUESTED", "TP_ACCEPTED", "EXPIRED_OFFER"].includes(s)) return;
      const requested = t.schedule?.requested_pickup;
      if (requested) {
        const puTs = new Date(requested).getTime();
        // Actionable window: PU is in the next 24h, or up to 30min in the past.
        if (Number.isFinite(puTs)) {
          if (puTs < now - 30 * MIN) return;
          if (puTs > now + 24 * 60 * MIN) return;
        }
      }
      const score = getOperationalState(t).attention_score;
      needs.push({
        kind: "unassigned", severity: s === "EXPIRED_OFFER" ? "high" : "med",
        attention_score: score,
        label: `${t.rider?.name || "trip"} · needs driver`,
        sub: statusPretty(s),
        trip_id: t.trip_id,
      });
    });

    // ─── 2 · ABOUT TO BECOME A PROBLEM ────────────────────────
    const risks = [];
    // Nova's drift alerts — a chained driver projected to miss their next
    // stop. Row carries Nova's suggested resolution for one-tap action.
    chainAlerts.forEach(a => {
      const opt = (a.options || []).find(o => o.type === "reassign_driver");
      const critical = (a.projected_delay_min || 0) > 15;
      risks.push({
        kind: "chain_alert", severity: critical ? "critical" : "high", attention_score: critical ? 90 : 80,
        label: `${a.rider_name || "next stop"} · running ~${a.projected_delay_min}m behind`,
        sub: opt ? `nova suggests: ${opt.name} (${opt.distance_mi}mi away)` : "no nearby driver found",
        alert_id: a.alert_id, suggested_option: opt,
      });
    });
    // Nova's general single-trip pickup-risk alerts — real ETA-vs-deadline
    // projection (not just wall-clock heuristics), covers every trip type
    // including CPD. Carries a candidate replacement driver when one exists.
    const riskAlertTripIds = new Set();
    tripRiskAlerts.forEach(a => {
      riskAlertTripIds.add(a.trip_id);
      const opt = (a.options || []).find(o => o.type === "reassign_driver");
      if (a.kind === "cascading_failure") {
        // A driver just declined and the remaining nearby supply is thin —
        // this is Nova catching the domino effect before it becomes a late
        // pickup, not after.
        risks.push({
          kind: "trip_risk_alert", severity: a.remaining_supply === 0 ? "critical" : "high",
          attention_score: a.remaining_supply === 0 ? 95 : 85,
          label: `${a.rider_name || "trip"}${a.trip_type === "cpd" ? " · CPD" : ""} · driver declined, ${a.remaining_supply} left nearby`,
          sub: opt ? `nova suggests: ${opt.name} (${opt.distance_mi}mi away)` : "no nearby replacement found",
          alert_id: a.alert_id, suggested_option: opt, trip_id: a.trip_id,
        });
        return;
      }
      risks.push({
        kind: "trip_risk_alert", severity: a.projected_delay_min > 15 ? "critical" : "high",
        attention_score: a.projected_delay_min > 15 ? 92 : 82,
        label: `${a.rider_name || "trip"}${a.trip_type === "cpd" ? " · CPD" : ""} · running ~${a.projected_delay_min}m behind`,
        sub: opt ? `nova suggests: ${opt.name} (${opt.distance_mi}mi away)` : `${a.current_driver?.name || "driver"} · no nearby replacement found`,
        alert_id: a.alert_id, suggested_option: opt, trip_id: a.trip_id,
      });
    });
    allTrips.forEach(t => {
      const s = String(t.status || "").toUpperCase();
      if (!["OFFERED", "ASSIGNED", "ACCEPTED", "EN_ROUTE", "ARRIVED_PU", "ON_TRIP"].includes(s)) return;
      // Nova already raised a real, ETA-projected alert for this trip — don't
      // also show the cruder wall-clock heuristic for the same trip.
      if (riskAlertTripIds.has(t.trip_id)) return;
      const requested = t.schedule?.requested_pickup;
      if (!requested) return;
      const puTs = new Date(requested).getTime();
      if (!Number.isFinite(puTs)) return;
      // Ignore trips whose PU time is more than 12h off in either direction —
      // that's stale test data or archival, not actionable now.
      const absOffset = Math.abs(puTs - now);
      if (absOffset > 12 * 60 * MIN) return;
      const untilPU = puTs - now;
      if (["OFFERED", "ASSIGNED", "ACCEPTED"].includes(s) && untilPU < 10 * MIN && untilPU > 0) {
        risks.push({
          kind: "late_departure_risk", severity: untilPU < 5 * MIN ? "high" : "med",
          attention_score: untilPU < 5 * MIN ? 75 : 55,
          label: `${t.rider?.name || "trip"} · driver not moving`,
          sub: `${Math.round(untilPU / MIN)}m to pickup`,
          trip_id: t.trip_id,
        });
      }
      if (["EN_ROUTE"].includes(s) && untilPU < 0) {
        risks.push({
          kind: "late_pickup_progressing", severity: "med", attention_score: 55,
          label: `${t.rider?.name || "trip"} · running behind`,
          sub: `${Math.abs(Math.round(untilPU / MIN))}m past PU`,
          trip_id: t.trip_id,
        });
      }
    });

    // ─── 3 · FAILING PROVIDERS ────────────────────────────────
    // Look at last-60-min TP-side outcomes on trips: TP_DECLINED, EXPIRED_OFFER
    const tpBad = new Map(); // tp_id → {name, bad, total}
    allTrips.forEach(t => {
      const tp = t.assigned_tp || {};
      const tpid = tp.tp_id;
      if (!tpid) return;
      const updated = new Date(t.updated_at || t.created_at || 0).getTime();
      if (updated < now - 60 * MIN) return;
      const s = String(t.status || "").toUpperCase();
      const entry = tpBad.get(tpid) || { tp_id: tpid, name: tp.tp_name || tpid, bad: 0, total: 0 };
      entry.total += 1;
      if (["EXPIRED_OFFER", "TP_DECLINED"].includes(s)) entry.bad += 1;
      // Also count timeline events
      (Array.isArray(t.timeline) ? t.timeline : []).forEach(e => {
        const evt = String(e.event || "").toUpperCase();
        if (evt === "TP_DECLINED") entry.bad += 1;
      });
      tpBad.set(tpid, entry);
    });
    const failingTps = Array.from(tpBad.values())
      .filter(x => x.bad >= 2)  // 2+ failures in the hour = worth surfacing
      .sort((a, b) => b.bad - a.bad)
      .slice(0, 3);

    // ─── 4 · LATE DRIVERS ─────────────────────────────────────
    const lateDrivers = [];
    allTrips.forEach(t => {
      const s = String(t.status || "").toUpperCase();
      if (!["EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(s)) return;
      const requested = t.schedule?.requested_pickup;
      if (!requested) return;
      const puTs = new Date(requested).getTime();
      const overBy = now - puTs;
      if (overBy < 2 * MIN) return; // grace
      if (overBy > 12 * 60 * MIN) return; // stale — not actionable
      const drv = t.assigned_driver || {};
      lateDrivers.push({
        kind: "late_driver", severity: overBy > 10 * MIN ? "high" : "med",
        label: `${drv.driver_name || drv.name || "driver"} · ${t.rider?.name || "trip"}`,
        sub: `${Math.round(overBy / MIN)}m past PU · ${statusPretty(s)}`,
        trip_id: t.trip_id,
      });
    });
    lateDrivers.sort((a, b) => (b.sub || "").localeCompare(a.sub || "")); // approx desc

    // ─── 5 · NEXT ACTION ──────────────────────────────────────
    const nextAction = pickNextAction(needs, risks);

    // ─── 6 · AI ACTIONS (last 5 min) ──────────────────────────
    const whispers = Array.isArray(network?.whispers) ? network.whispers : [];
    const aiActions = whispers
      .filter(w => {
        const at = w.at ? new Date(w.at).getTime() : now;
        return at > now - 5 * MIN;
      })
      .filter(w => /nova|auto|assign|accepted|declined|dispatched/i.test(w.text || ""))
      .slice(-6)
      .reverse();

    // ─── 7 · LAST MINUTE CHANGES ──────────────────────────────
    const minuteChanges = whispers
      .filter(w => {
        const at = w.at ? new Date(w.at).getTime() : now;
        return at > now - MIN;
      })
      .slice(-8)
      .reverse();

    // Sort needs/risks by attention_score — one number, every surface agrees.
    needs.sort((a, b) => (b.attention_score ?? 0) - (a.attention_score ?? 0));
    risks.sort((a, b) => (b.attention_score ?? 0) - (a.attention_score ?? 0));

    return {
      needs, risks, failingTps, lateDrivers,
      nextAction, aiActions, minuteChanges,
      counts: {
        needs: needs.length,
        risks: risks.length,
        late: lateDrivers.length,
        failing_tps: failingTps.length,
      },
    };
  }, [allTrips, incomingOffers, network, sosTripId, chainProposals, chainAlerts, tripRiskAlerts]);
}

function pickNextAction(needs, risks) {
  // 1st SOS trumps everything
  const sos = needs.find(n => n.kind === "sos");
  if (sos) return { ...sos, verb: "acknowledge SOS now" };
  // Everything else: one attention_score ranks every candidate — no more
  // separate severity ladders for needs vs. risks.
  const candidates = [...needs, ...risks].filter(n => (n.attention_score ?? 0) >= 50);
  if (!candidates.length) return null;
  candidates.sort((a, b) => (b.attention_score ?? 0) - (a.attention_score ?? 0));
  const top = candidates[0];
  const verb = top.kind === "kaizen_offer" ? "accept or decline"
    : top.kind === "chain_proposal" ? "approve nova's route"
    : top.kind === "chain_alert" ? "resolve with nova's option"
    : top.kind === "trip_risk_alert" ? "resolve with nova's option"
    : top.kind === "unassigned" ? "assign a driver"
    : top.kind?.includes("late") || top.kind?.includes("departure") ? "check on this trip"
    : "handle";
  return { ...top, verb };
}

function ageSeconds(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function statusPretty(s) {
  return String(s || "").toLowerCase().replace(/_/g, " ");
}
